import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ChipGroup, Field, SectionLabel, CheckRow } from "@/components/site/FormBits";
import { AddOnPicker } from "@/components/site/AddOnPicker";
import { BookCallStrip } from "@/components/site/BookCall";
import { api, formatApiError } from "@/lib/api";
import { JOURNEY_STAGES, LEAD_SKILLS, LEAD_COLLAB, CONTACT_PREFS } from "@/lib/constants";

const EMPTY = {
  journey_stage: "", goals: "", skills: [], offer: "", collaboration: [], existing_project: "",
  name: "", email: "", whatsapp: "", whatsapp_consent: false, contact_pref: "Email", retreat_optin: false,
  add_ons: [], add_ons_other: "",
};

function LeadSuccess({ result }) {
  const q = `email=${encodeURIComponent(result.email)}&next=/apply`;
  return (
    <div className="text-center py-10 px-4" data-testid="lead-form-success">
      <CheckCircle2 className="h-12 w-12 text-[#1A9AA8] mx-auto" />
      <h3 className="mt-4 text-2xl font-bold text-[#0B2A4A]">Transmission received.</h3>
      <p className="mt-3 text-slate-600 max-w-md mx-auto">
        Your student profile has been started. Mr. Greg will review it and reply by your preferred contact method.
        {result.has_account ? " Sign in to apply for the 7-day course." : " Create your account with the same email to apply for the 7-day beginners course."}
      </p>
      <Link to={result.has_account ? `/login?${q}` : `/signup?${q}`} className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#F08A3C] text-white px-6 py-3 font-semibold hover:bg-[#D96F28]" data-testid="lead-success-continue-btn">
        {result.has_account ? "Sign in & apply" : "Create my student account"} <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}

export function LeadForm() {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [more, setMore] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.journey_stage) return toast.error("Please choose where you are in your journey");
    setBusy(true);
    try {
      const { data } = await api.post("/leads", f);
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (result) return <LeadSuccess result={result} />;
  return (
    <form onSubmit={submit} className="flex flex-col gap-8" data-testid="lead-form">
      <BookCallStrip source="lead-form" />
      <div className="flex flex-col gap-5">
        <SectionLabel>01 · Your journey</SectionLabel>
        <Field label="Where are you in your journey?">
          <ChipGroup options={JOURNEY_STAGES} value={f.journey_stage} onChange={set("journey_stage")} multi={false} testId="lead-form-journey-stage" />
        </Field>
        <Field label="What are you working toward?" htmlFor="lead-goals">
          <Textarea id="lead-goals" rows={3} value={f.goals} onChange={set("goals")} placeholder="A remote job, a freelance service, a project in Nicaragua..." data-testid="lead-form-goals" />
        </Field>
      </div>
      <div className="flex flex-col gap-5">
        <button type="button" onClick={() => setMore((m) => !m)} aria-expanded={more} className="flex items-center justify-between rounded-xl border border-[#0B2A4A]/10 bg-[#FAF6EE] px-4 py-3 text-left hover:bg-[#F5ECD7] transition-colors" data-testid="lead-form-more-toggle">
          <span><SectionLabel>02 · What you bring</SectionLabel><span className="mt-1 block text-sm text-slate-600">Skills, collaboration and add-ons (optional)</span></span>
          <ChevronDown className={`h-5 w-5 text-[#0B2A4A] transition-transform ${more ? "rotate-180" : ""}`} />
        </button>
        {more && (
        <div className="flex flex-col gap-5" data-testid="lead-form-more">
        <Field label="Which skills do you bring? (pick any)">
          <ChipGroup options={LEAD_SKILLS} value={f.skills} onChange={set("skills")} testId="lead-form-skills" />
        </Field>
        <Field label="What can you offer the team or project?" htmlFor="lead-offer">
          <Textarea id="lead-offer" rows={2} value={f.offer} onChange={set("offer")} data-testid="lead-form-offer" />
        </Field>
        <Field label="What kind of collaboration are you looking for?">
          <ChipGroup options={LEAD_COLLAB} value={f.collaboration} onChange={set("collaboration")} testId="lead-form-collab-type" />
        </Field>
        <Field label="Have an existing project or business you'd like help growing? (optional)" htmlFor="lead-project">
          <Textarea id="lead-project" rows={2} value={f.existing_project} onChange={set("existing_project")} data-testid="lead-form-existing-project" />
        </Field>
        <AddOnPicker value={f.add_ons} other={f.add_ons_other} onChange={set("add_ons")} onOtherChange={set("add_ons_other")} testId="lead-form-addons" />
        </div>
        )}
      </div>
      <div className="flex flex-col gap-5">
        <SectionLabel>03 · How to reach you</SectionLabel>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Your Name" htmlFor="lead-name">
            <Input id="lead-name" required value={f.name} onChange={set("name")} data-testid="lead-form-name" />
          </Field>
          <Field label="Email" htmlFor="lead-email">
            <Input id="lead-email" type="email" required value={f.email} onChange={set("email")} data-testid="lead-form-email" />
          </Field>
        </div>
        <Field label="WhatsApp number" htmlFor="lead-wa" hint="Include country code, e.g. +505 ...">
          <Input id="lead-wa" type="tel" required pattern="[0-9+\s\(\)\.\-]{7,}" title="Enter your WhatsApp number with country code" value={f.whatsapp} onChange={set("whatsapp")} data-testid="lead-form-whatsapp" />
        </Field>
        {f.whatsapp && (
          <CheckRow checked={f.whatsapp_consent} onChange={set("whatsapp_consent")} testId="lead-form-whatsapp-consent">
            I agree that Mr. Greg may contact me on WhatsApp.
          </CheckRow>
        )}
        <Field label="Preferred way to be contacted">
          <ChipGroup options={CONTACT_PREFS} value={f.contact_pref} onChange={set("contact_pref")} multi={false} testId="lead-form-contact-pref" />
        </Field>
        <CheckRow checked={f.retreat_optin} onChange={set("retreat_optin")} testId="lead-form-retreat-optin">
          Also keep me posted about digital nomad retreats and events in Nicaragua.
        </CheckRow>
      </div>
      <button disabled={busy} className="self-start inline-flex items-center gap-2 rounded-xl bg-[#F08A3C] text-white px-7 py-3.5 font-semibold shadow-md hover:bg-[#D96F28] hover:-translate-y-0.5 transition-transform disabled:opacity-60" data-testid="lead-form-submit-btn">
        {busy ? "Sending..." : "Send Transmission"} <ArrowRight className="h-4 w-4" />
      </button>
    </form>
  );
}
